import React from "react";
import { getSportMeta } from "../../lib/sportCategories";

/**
 * Compact badge that shows a sport's emoji + short label.
 * Use on event cards, listings, community posts to signal which tribe
 * a record belongs to.
 *
 * Variants:
 *  - "solid" (default): full sport-colour fill, white text. Good for hero placements.
 *  - "ghost": transparent background, sport-coloured border + text. Good when
 *    the badge sits on a card body (not on top of an image) and shouldn't
 *    compete visually with the price / title.
 */
export const SportTagBadge = ({
  sport,
  size = "md",
  showLabel = true,
  variant = "solid",
  className = "",
}) => {
  const meta = getSportMeta(sport);
  const sizeCls =
    size === "xs"
      ? "text-xs px-1.5 py-0.5 gap-1"
      : size === "sm"
      ? "text-xs px-2 py-0.5 gap-1"
      : "text-sm px-2.5 py-1 gap-1.5";

  const isGhost = variant === "ghost";
  const ghostStyle = {
    backgroundColor: "transparent",
    border: `1px solid ${meta.color}66`,
    color: meta.color,
  };
  const solidStyle = { backgroundColor: meta.color };

  return (
    <span
      data-testid={`sport-badge-${sport}`}
      className={`inline-flex items-center rounded-full font-medium ${
        isGhost ? "" : "text-white shadow-sm"
      } ${sizeCls} ${className}`}
      style={isGhost ? ghostStyle : solidStyle}
      aria-label={`Sport: ${meta.label}`}
    >
      {meta.emoji && <span aria-hidden>{meta.emoji}</span>}
      {showLabel && <span>{meta.short}</span>}
    </span>
  );
};

export default SportTagBadge;
